import type { Campaign, Collab, CampaignType } from '@foodfluence/shared';
import { isBrandManagedProductCompensation } from '../../../utils/brandManagedCampaignUtils';
import { resolveCollabProductValue } from '@foodfluence/shared';
import { collabProductLabel } from 'features/collabs/utils/collabProductLabel';

export type WalletCompensationKind = 'cash' | 'credit' | 'product_value' | 'product_reimbursement';

export interface WalletCompensationLine {
  kind: WalletCompensationKind;
  label: string;
  amount: number;
  /** Included in Whop wallet sweep (cash net of fee, retail product reimbursement). */
  withdrawable: boolean;
}

function formatUsd(amount: number): string {
  return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
}

function isRetailCampaignType(campaignType?: CampaignType): boolean {
  return campaignType === 'retail';
}

function isShippingCampaignType(campaignType?: CampaignType): boolean {
  return campaignType === 'shipToHome' || campaignType === 'shopify';
}

/** Per-line compensation for a completed collab — labels match collab details terminology. */
export function getCollabCompensationBreakdown(collab: Collab, campaign?: Campaign): WalletCompensationLine[] {
  const lines: WalletCompensationLine[] = [];

  const cash = collab.cash ?? 0;
  if (cash > 0) {
    lines.push({
      kind: 'cash',
      label: 'Cash',
      amount: cash,
      withdrawable: true,
    });
  }

  const credit = collab.credit ?? 0;
  if (credit > 0) {
    lines.push({
      kind: 'credit',
      label: 'Store credit',
      amount: credit,
      withdrawable: false,
    });
  }

  // The creator's own money view: what they chose, not the campaign's estimate.
  const productValue = resolveCollabProductValue(collab, campaign);
  if (productValue > 0) {
    if (isRetailCampaignType(campaign?.campaignType)) {
      lines.push({
        kind: 'product_reimbursement',
        label: 'Product reimbursement',
        amount: productValue,
        withdrawable: true,
      });
    } else if (
      isShippingCampaignType(campaign?.campaignType) ||
      isBrandManagedProductCompensation(campaign, collab)
    ) {
      lines.push({
        kind: 'product_value',
        label: collabProductLabel(collab, campaign)
          ? `Product — ${collabProductLabel(collab, campaign)}`
          : 'Product value',
        amount: productValue,
        withdrawable: false,
      });
    } else {
      lines.push({
        kind: 'product_value',
        label: 'Product value',
        amount: productValue,
        withdrawable: false,
      });
    }
  }

  return lines;
}

export function sumCompensationLines(lines: WalletCompensationLine[]): number {
  return lines.reduce((sum, line) => sum + line.amount, 0);
}

/** Compact subtitle, e.g. "$100 cash · $50 store credit · $25 product reimbursement". */
export function formatCompensationBreakdown(lines: WalletCompensationLine[]): string {
  return lines.map((line) => `${formatUsd(line.amount)} ${line.label.toLowerCase()}`).join(' · ');
}
